import { ReclamationAccessPolicy } from 'graphql-ts-types';

import { ReclamationStep } from 'common/enums/Reclamation';
import type { StepOptions } from '../gql/mutation/types';
import type { StepWithArticle } from '../types/drawer';

export const deleteOptionalSteps = (
  { reclamationId, itemId }: StepWithArticle,
  steps: StepWithArticle[]
) =>
  steps.filter(step => {
    const isNotSameLineNoStep = step.reclamationId !== reclamationId || step.itemId !== itemId;
    const isReasonsStep =
      step.type === 'reasons' && step.reclamationId === reclamationId && step.itemId === itemId;

    return isNotSameLineNoStep || isReasonsStep;
  });

type StepsCalculationOptions = StepOptions['data'];

const getOptionalSteps = (
  stepProps: StepWithArticle,
  {
    attachmentPolicy,
    partNumberPolicy,
    chooseSolutionAllowed,
    commentPolicy,
  }: StepsCalculationOptions
) => {
  const optionalSteps: StepWithArticle[] = [];
  const isAdditionalInfoNeeded =
    attachmentPolicy === ReclamationAccessPolicy.Required ||
    partNumberPolicy !== ReclamationAccessPolicy.Disallowed ||
    commentPolicy !== ReclamationAccessPolicy.Disallowed;

  if (chooseSolutionAllowed) {
    optionalSteps.push({
      ...stepProps,
      type: ReclamationStep.Solutions,
    });
  }

  if (isAdditionalInfoNeeded) {
    optionalSteps.push({
      ...stepProps,
      type: ReclamationStep.AdditionalInfo,
    });
  }

  return optionalSteps;
};

type GetRemainingStepsResponse = [boolean, StepWithArticle[]];

type GetRemainingSteps = {
  activeStep: StepWithArticle;
  steps: StepWithArticle[];
  options: StepsCalculationOptions;
};

export const getRemainingSteps = ({
  activeStep,
  steps,
  options,
}: GetRemainingSteps): GetRemainingStepsResponse => {
  const newSteps = deleteOptionalSteps(activeStep, steps);
  const optionalSteps = getOptionalSteps(activeStep, options);
  const isChangeNeeded = newSteps.length !== steps.length || !!optionalSteps.length;

  if (optionalSteps.length) {
    const activeStepIndex = steps.findIndex(step => step === activeStep);
    newSteps.splice(activeStepIndex + 1, 0, ...optionalSteps);
  }

  return [isChangeNeeded, newSteps];
};
